/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    type CSSProperties,
    type FormEvent,
    type ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import debounce from 'lodash.debounce';
import { Search as SearchIcon, X as XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import BgSuggestions from '@/components/search/bg-suggestions';
import { useNavigate } from '@/hooks/use-navigate';
import { useSearchSuggestions } from '@/hooks/use-search-suggestions';
import { useTransformSearchSuggestions } from '@/hooks/use-transform-search-suggestions';

/** Below this many characters the suggestions endpoint is not called at all. */
const SUGGESTION_MIN_LENGTH = 3;

/**
 * BG's search — a MODAL SURFACE, not a dropdown.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE DIVERGENCE THIS FILE EXISTS TO CLOSE
 *
 * What ours did before: rendered `<div className="section-container">` in NORMAL
 * FLOW inside the header, holding NM's `SearchBar`. Opening it REFLOWED the page
 * — the header stayed put and every band below it was pushed down.
 *
 * What live BG does, at both forms:
 *
 *   MOBILE  `.reference/captures/bg-searchbar-m-375/App.tsx`
 *           an opaque white panel `h-[844px] left-0 top-[-70px]` — a full
 *           viewport of white, pulled UP over the ~70px compact header row.
 *           The header is NOT unmounted: the burger, the wordmark and the bag
 *           link are all still in the capture's document order; the panel is
 *           their sibling and COVERS them. The trigger's own slot goes empty.
 *
 *   DESKTOP `.reference/live-shots/live-bg-search-bar.png` (owner's browser,
 *           viewport ≈1443 CSS px). A left-edge drawer at x=0, full viewport
 *           height from y=0, right edge at ≈402 — with the rest of the page
 *           DIMMED BY A SCRIM and still legible through it.
 *
 * The mobile capture's "covers rather than unmounts" is what makes a FOCUS TRAP
 * structural rather than a nicety: without one, Tab walks straight into the
 * header links sitting invisible underneath the panel.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY Radix Dialog (via `ui/sheet`) AND NOT A HAND-ROLLED OVERLAY
 *
 * Focus trap, initial focus, focus restored to the trigger on close, scroll lock
 * on the body, `role="dialog"` + `aria-modal`, Escape-to-close and
 * scrim-click-to-close are ONE primitive's job, and `ui/sheet` already carries
 * the two escape hatches this surface needs — `closeButton={null}` (BG's close
 * is not where the stock one is) and `overlayClassName` (the scrim is a portal
 * SIBLING, unreachable from `className`). Hand-rolling any of it would be a
 * worse focus trap with the same markup.
 *
 * DELIBERATE DIVERGENCE FROM LIVE, flagged not rationalised — the same class of
 * divergence `search-panel-trigger.tsx` already documents for the trigger:
 *
 *   · Live's close control is `<input type="reset" aria-label="Close search">`.
 *     A form reset whose visible job is CLOSE: the name and the behaviour
 *     disagree. Ours is a real `<button type="button">`.
 *   · Live carries neither `role="dialog"` nor `aria-modal`. Both forms ARE
 *     modal — mobile occludes the page entirely, desktop dims it behind a scrim
 *     — so assistive tech is told the truth rather than live's omission.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE BREAKPOINT AT WHICH THE TWO FORMS SWAP — MEASURED ON LIVE
 *
 * The panel is a full-viewport takeover through 1024 and becomes the left-edge
 * drawer at 1025. Observed directly on live BG (2026-08-20): full at 1024, and
 * ~403px at 1025.
 *
 * That boundary is NOT a Tailwind breakpoint. `lg:` fires AT 1024, one pixel
 * early, which would put the drawer on the last width that live still takes
 * over. So the gates below are spelled `bgnav:` rather
 * than borrowed from the scale — the extra verbosity is the point, since a
 * later reader reaching for `lg:` would be silently wrong at exactly one width.
 *
 * It is also NOT where BG's header composition switches (`xl`, 1280). This
 * header now has three boundaries that do not coincide: wordmark art at `md`,
 * search panel form at 1025, header composition at `xl`. Each was measured
 * separately; do not collapse them.
 *
 * An earlier revision assumed `xl` here, reasoning that a takeover built to
 * cover the compact header would end where that header ends. The reasoning was
 * sound and the answer was wrong — the takeover outlives the compact header by
 * 256px.
 *
 * DRAWER WIDTH is a fixed 403px, not a fraction. Read as ~28% off a 1440-wide
 * shot, which is what 403 is at that width; the 1025 observation separates the
 * two, since a 28% rule would give 287 there.
 *
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHERE THE MOBILE PANEL'S TOP EDGE GOES — CLOSED 2026-08-21
 *
 * Live's MOBILE panel leaves the promo ticker visible above it and covers the
 * compact header row. Owner capture `.reference/live-shots/live-bg-mob-search-bar.png`,
 * taken at an emulated 390x844: the black ticker band paints in full at the top,
 * the white panel begins flush under it, and the burger/wordmark/bag row is gone.
 *
 * THE OFFSET IS NOT THE TICKER'S HEIGHT — IT IS THE HEADER'S TOP EDGE, and that
 * distinction is the whole reason this sat open. Live states it that way itself:
 * `.reference/captures/bg-searchbar-m-375/App.tsx` puts the panel IN FLOW after
 * the header as `h-[844px] left-0 top-[-70px]`, and 70 is exactly that capture's
 * own header row (`h-[69px]` plus its 1px bottom border). A panel pulled up by
 * the header's height lands on the header's TOP — which is the ticker's bottom
 * when the ticker is on screen, and 0 when it is not.
 *
 * WHY THE HEIGHT WOULD HAVE BEEN WRONG. An earlier note here declined a fixed
 * offset because the ticker's height "is neither constant nor guaranteed to be
 * on screen". The conclusion was right and the reason was too weak — measured on
 * our own app at 390 (BG search and BG home, both `data-header-sticky="true"`):
 *
 *   scrollTop 0     header box y=72 h=70   ticker occupies 0..72
 *   scrollTop 900   header box y=0  h=70   ticker GONE from the viewport
 *
 * The ticker is a SIBLING above `<header>` (mounted in `routes/_app`), not a
 * child of it, so the sticky header pins itself to the top and leaves the ticker
 * behind. A `top: 72px` would therefore have opened a 72px strip of live page
 * above an opaque panel on every scrolled open, on every route — sticky or not.
 * Reading the header's own top is correct in both states and needs no ticker
 * hook, no height token and no per-route knowledge.
 *
 * THE UNMEASURED STATE DEGRADES TO THE STATUS QUO, deliberately. No live capture
 * exists of BG mobile search opened on a SCROLLED page, so what live does with
 * no ticker to spare is unknown. Clamping at 0 makes that case render exactly
 * what shipped before this change, which is the safest available answer to a
 * question the evidence cannot settle.
 *
 * DESKTOP IS UNAFFECTED and that is measured, not assumed: in
 * `.reference/live-shots/live-bg-search-bar.png` (owner's browser, live URL in
 * the address bar) the drawer's white column runs to the very top and the ticker
 * is visible only to its RIGHT, dimmed by the scrim. So the offset is gated to
 * the same 1025 boundary as the rest of this file's mobile/desktop split.
 */
export default function BgSearchPanel({
    open,
    onOpenChange,
    panelId,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Must match the trigger's `aria-controls`. */
    panelId: string;
}): ReactElement {
    /**
     * The header's current top edge, read in the RENDER that opens the panel.
     *
     * WHY HERE AND NOT IN AN EFFECT. The value has to be applied in the same
     * commit that mounts the panel; an effect runs after paint, so the panel
     * would paint once at the top of the viewport and then jump down by the
     * ticker's height. `open` is false during SSR — the panel renders nothing
     * then — so this branch is client-only and cannot produce a hydration
     * mismatch. It is the same render-phase DOM read, with the same
     * `typeof document` guard, that the focus-restore ref below already makes,
     * and for the same reason: a later moment is the wrong moment.
     *
     * Floor rather than round: if the panel starts a sub-pixel LOW, a hairline
     * of page shows above an opaque surface, and if it starts a sub-pixel HIGH
     * it covers a hairline of the ticker instead. Only one of those is visible.
     */
    const headerInsetTop =
        open && typeof document !== 'undefined'
            ? Math.max(0, Math.floor(document.querySelector('header')?.getBoundingClientRect().top ?? 0))
            : 0;

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                id={panelId}
                data-testid="header-search-panel"
                side="left"
                // Consumed by the two mobile classes below. A custom property
                // rather than a `top`/`height` pair written straight into
                // `style` so the 1025 breakpoint stays in CSS with the rest of
                // this surface's mobile/desktop split — a `matchMedia` here
                // would be a second copy of a boundary the file already warns
                // must not be collapsed with the other two.
                style={{ '--bg-search-panel-inset-top': `${headerInsetTop}px` } as CSSProperties}
                // Radix hides the rest of the document from assistive tech with
                // `aria-hidden` rather than announcing the dialog as modal, so
                // the attribute is stated explicitly. Both forms genuinely ARE
                // modal — mobile occludes the page, desktop dims it.
                aria-modal="true"
                // BG's close is at the panel's left at mobile and at the
                // drawer's top-RIGHT at desktop — neither is where the stock
                // top-right control sits at mobile, so the built-in is removed
                // and this surface renders its own (below).
                closeButton={null}
                // THE SCRIM. Desktop only, because that is the only form live
                // dims anything in. It is also what keeps the ticker looking
                // right now that the panel starts below it: the overlay spans
                // the full viewport at every width, so a dimmed one would grey
                // out the strip of ticker this surface deliberately leaves on
                // screen, and live's ticker is NOT dimmed at mobile. The
                // element itself stays mounted at every width — it is what
                // makes an outside pointer-down dismiss the surface.
                overlayClassName="bg-transparent bgnav:bg-black/50"
                className={[
                    // MOBILE: an opaque takeover of everything BELOW the ticker.
                    'w-full max-w-none sm:max-w-none border-r-0 p-0 gap-0 bg-background',
                    // The top edge, and the height that follows from it. `top`
                    // displaces the primitive's `inset-y-0`, so the height is
                    // stated rather than left to a `bottom` that is no longer
                    // there. Both collapse to the previous full-viewport box
                    // whenever the inset reads 0 — which is every scrolled open,
                    // and every render before this value is measured.
                    'top-(--bg-search-panel-inset-top) h-[calc(100%_-_var(--bg-search-panel-inset-top))]',
                    // DESKTOP takes the ticker with it (live-bg-search-bar.png),
                    // so the inset is dropped rather than merely unset.
                    'bgnav:top-0 bgnav:h-full',
                    // DESKTOP: a left-edge drawer, full height, 403px wide.
                    // FIXED rather than the 28% the single shot also fits: the
                    // drawer's only content is a one-line input, nothing in it
                    // scales with the viewport, and 28% of a 1920 viewport is
                    // 538px of empty white. A second desktop shot at another
                    // width is what would change that.
                    'bgnav:w-[403px] bgnav:border-r',
                ].join(' ')}
                // The surface has no descriptive paragraph — live's panel is an
                // input and a list. Passing `undefined` explicitly is Radix's
                // documented way to say so; omitting it logs a warning instead.
                aria-describedby={undefined}>
                {/* EVERY HOOK THIS SURFACE NEEDS LIVES ONE LEVEL DOWN, and that
                    is load-bearing rather than tidiness. `SheetContent` portals
                    through Radix's `Presence`, which renders NOTHING while the
                    dialog is closed — so a body mounted here runs its data,
                    navigation and config hooks only while the panel is open.
                    Hoisting them into the shell instead would run `useNavigate`
                    (and with it the site/locale resolution it depends on) on
                    every BG header render at every route, for a surface nobody
                    has opened. It also gives the field its reset for free: the
                    body unmounts on close, so the next open starts empty
                    without a single line of teardown. */}
                <BgSearchPanelBody panelId={panelId} onClose={() => onOpenChange(false)} />
            </SheetContent>
        </Sheet>
    );
}

/**
 * The panel's contents — mounted only while the dialog is open (see the comment
 * at its call site). Everything below this line is BG's own chrome: none of NM's
 * `SearchBar` reaches this surface.
 */
function BgSearchPanelBody({ panelId, onClose }: { panelId: string; onClose: () => void }): ReactElement {
    const { t } = useTranslation('header');
    const navigate = useNavigate();
    const config = useConfig();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [query, setQuery] = useState('');

    const trimmed = query.trim();
    const { data: suggestions, refetch } = useSearchSuggestions({
        q: query,
        includeEinsteinSuggestedPhrases: true,
        enabled: trimmed.length >= SUGGESTION_MIN_LENGTH,
    });
    // No `useSuggestionProductImages` backfill on this path ON PURPOSE: BG's
    // rows are text-only, so the second getProducts call that exists purely to
    // resolve 40x40 thumbnails would be a request whose result nothing renders.
    const transformedSuggestions = useTransformSearchSuggestions(suggestions);
    // `hasRows` used to live here, feeding the input's `aria-expanded`. It went
    // out with the combobox ARIA below — the only thing that read it — rather
    // than being left as a computed value nothing consumes.

    const queryRef = useRef(query);
    const refetchRef = useRef<() => Promise<void>>(() => Promise.resolve());
    useEffect(() => {
        queryRef.current = query;
        refetchRef.current = refetch;
    }, [query, refetch]);

    const debouncedRefetch = useMemo(
        () =>
            debounce(() => {
                if (queryRef.current.trim().length >= SUGGESTION_MIN_LENGTH) {
                    void refetchRef.current();
                }
            }, config.pages.search.suggestionsDebounce),
        [config.pages.search.suggestionsDebounce]
    );
    useEffect(() => {
        if (trimmed.length >= SUGGESTION_MIN_LENGTH) {
            debouncedRefetch();
        } else {
            debouncedRefetch.cancel();
        }
        return () => debouncedRefetch.cancel();
    }, [trimmed, debouncedRefetch]);

    /**
     * WHERE FOCUS GOES ON OPEN, AND WHERE IT COMES BACK TO ON CLOSE.
     *
     * Captured during this body's FIRST RENDER, which is the last moment before
     * Radix's `FocusScope` moves focus into the dialog — every render in a
     * commit precedes every effect in it, and this body mounts in the same
     * commit as the scope above it. So this ref holds the trigger, not the
     * panel.
     *
     * WHY WE RESTORE IT OURSELVES RATHER THAN LEAVING IT TO RADIX. Radix's
     * modal `DialogContent` overrides close-auto-focus with, in effect,
     * `event.preventDefault(); context.triggerRef.current?.focus()` — it
     * cancels `FocusScope`'s own restore and hands focus to its `Dialog.Trigger`
     * instead. This surface has no `Dialog.Trigger`: the control lives in the
     * header bar, outside the dialog, so that ref is null and the restore
     * becomes a no-op. MEASURED at 390 before this was added — focus sat on the
     * close button through the exit animation and then fell to `<body>`, which
     * drops a keyboard shopper back at the top of the document.
     */
    const restoreFocusRef = useRef<HTMLElement | null>(null);
    if (restoreFocusRef.current === null && typeof document !== 'undefined') {
        restoreFocusRef.current = document.activeElement as HTMLElement | null;
    }
    useEffect(() => {
        // The panel exists to be typed into, so the caret lands in the field
        // rather than on the dialog container.
        inputRef.current?.focus();
        return () => {
            const previous = restoreFocusRef.current;
            if (previous && previous !== document.body && previous.isConnected) {
                previous.focus();
            }
        };
    }, []);

    const close = useCallback(() => onClose(), [onClose]);

    const handleSubmit = useCallback(
        (event: FormEvent) => {
            event.preventDefault();
            const searchQuery = query.trim();
            if (!searchQuery) return;
            close();
            void navigate(`/search?q=${encodeURIComponent(searchQuery)}`, { state: { query: searchQuery } });
        },
        [close, navigate, query]
    );

    // Suggestion rows are real anchors (see suggestions-list's `as="link"`), so
    // the ANCHOR navigates and this callback only tears the surface down.
    const closeOnSelect = useCallback(() => close(), [close]);

    const closeLabel = t('closeSearchPanel', { defaultValue: 'Close search' });
    const searchLabel = t('searchPlaceholder', { defaultValue: 'Search' });

    return (
        <>
            {/* The dialog's accessible name. Live's panel has no visible
                heading, so it is screen-reader only — a name the trap needs
                and the design does not paint. */}
            <SheetTitle className="sr-only">{searchLabel}</SheetTitle>

            <form onSubmit={handleSubmit} className="relative flex h-full flex-col" role="search">
                {/* THE CLOSE CONTROL — one control, two positions.
                        MOBILE: `left-6 top-4`, a 24px box INSIDE the input's own
                        left padding, not a flex sibling of it. Live's ✕ is at
                        `left-6 w-6 h-6` while the field starts at `ml-[24px]`
                        with `pl-[30px]` reserved, so the field's underline runs
                        UNDERNEATH the glyph. Two flex children would put the
                        underline to the right of the ✕ and is the wrong build.
                        Corroborated at 390 against the owner's own shot: ✕ left
                        edge 22.7 vs the capture's 24, box 24 vs 24.
                        DESKTOP: the opposite side and a different ROW — the
                        drawer's top-right, above the input. That asymmetry is
                        live's, read off live-bg-search-bar.png, not a slip. */}
                <button
                    type="button"
                    onClick={close}
                    aria-label={closeLabel}
                    data-testid="bg-search-panel-close"
                    className="absolute z-10 flex size-6 cursor-pointer items-center justify-center focus-ring-button left-6 top-4 bgnav:left-auto bgnav:right-6 bgnav:top-5">
                    <XIcon className="size-5" aria-hidden="true" />
                </button>

                {/* THE INPUT ROW.
                        Underlined — a bottom border only, never a boxed field:
                        both live forms show a rule under the text and no other
                        edge. Inset `mx-6` at mobile, which with the 48px of
                        combined margin reproduces the capture's
                        `w-[calc(100%-48px)]` (underline 24 → 366 at 390; the
                        owner's shot measures 22.7 → 365.6).
                        DESKTOP adds the magnifier to the input's LEFT — present
                        on live desktop, absent on live mobile, which is the
                        exact inverse of what ours used to render — and drops
                        below the close row via `bgnav:mt-16`. */}
                <div className="relative mx-6 mt-4 flex items-center border-b border-foreground bgnav:mt-16">
                    <SearchIcon className="mr-2 hidden size-4 shrink-0 bgnav:block" aria-hidden="true" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={searchLabel}
                        aria-label={searchLabel}
                        // NO COMBOBOX ARIA — see the matching note in
                        // header/search.tsx. This input declared
                        // `role="combobox"`, `aria-autocomplete="list"`,
                        // `aria-expanded` and an `aria-controls` pointing at
                        // the results div below; that div is a plain container
                        // with no `role="listbox"` and its rows are anchors
                        // with no `role="option"`. Nothing sets
                        // `aria-activedescendant` and this file has no key
                        // handler, so Down did nothing while a screen reader
                        // announced an expanded combobox. WCAG 4.1.2.
                        // Removed rather than completed: the honest
                        // announcement for what this is today is a labelled
                        // text input.
                        data-testid="bg-search-panel-input"
                        // `pl-[30px]` is the clearance live reserves for the
                        // ✕ sitting inside the field at MOBILE; desktop's ✕
                        // is on another row, so the padding goes away with
                        // it. No box, no ring — the row owns the underline.
                        className="w-full bg-transparent pl-[30px] outline-none bgnav:pl-0"
                    />
                    {/* CLEAR — the lowercase WORD, not a glyph, at the
                            input's right, mirroring the ✕ at the left. It
                            appears ONLY on a non-empty field: `aria-label="Clear
                            search input"` is absent from the empty capture
                            entirely and present in the typed one, which is a
                            hard state gate read off two captures rather than
                            inferred from a class-name modifier. */}
                    {query.length > 0 && (
                        <button
                            type="button"
                            onClick={() => {
                                setQuery('');
                                inputRef.current?.focus();
                            }}
                            aria-label={t('clearSearchInput', { defaultValue: 'Clear search input' })}
                            data-testid="bg-search-panel-clear"
                            className="ml-2 shrink-0 cursor-pointer lowercase focus-ring-button">
                            {t('clearSearchInputLabel', { defaultValue: 'clear' })}
                        </button>
                    )}
                </div>

                {/* RESULTS. Text rows only, no heading above them, and
                        NOTHING AT ALL in the empty state — both live captures'
                        empty panels contain zero content below the input, blank
                        white to the bottom of the viewport. `BgSuggestions`
                        returns null rather than a recent-searches block, which
                        is what ours used to put here. */}
                <div
                    id={`${panelId}-suggestions`}
                    className="mt-4 flex-1 overflow-y-auto px-6"
                    data-testid="bg-search-panel-results">
                    <BgSuggestions searchSuggestions={transformedSuggestions} closeAndNavigate={closeOnSelect} />
                </div>
            </form>
        </>
    );
}
